document.addEventListener('DOMContentLoaded', () => {
    const CenterLocatorApp = {
        // --- STATE & CONFIG ---
        state: {
            map: null,
            markerClusterer: null,
            userCoordinates: null,
            pendingMapUpdate: null, // Used if filters are applied before clusterer is ready
            allLocations: [], // Will store {id, title, ..., element, matchesPrimaryFilters}
            isDataLoaded: false,
            savedMapPosition: null,
        },
        config: {
            extendedVersion: window.centerLocator?.extendedVersion,
            apiKey: window.centerLocator?.apiKey,
            ajaxUrl: window.centerLocator?.ajax_url,
            nonce: window.centerLocator?.nonce,
            defaultCenter: window.centerLocator?.defaultCenter ?? { lat: 47.066667, lng: 2.4 },
            defaultZoom: parseInt(window.centerLocator?.defaultZoom) ?? 5,
            userZoom: parseInt(window.centerLocator?.userZoom) ?? 9,
            customCenter: window.centerLocator?.customCenter ?? false,
            markerIcon: {
                url: window.centerLocator?.icon,
                size: { width: 40, height: 40 }
            },
            disableTextSearch: window.centerLocator?.disableTextSearch,
            strings: window.centerLocator?.strings
        },
        elements: {},

        // --- INITIALIZATION ---
        init() {
            if (!window.centerLocator) {
                console.error('Localization data (centerLocator) is not defined.');
                return;
            }

            this.elements = {
                locatorWrapper: document.querySelector('.centers-locator'),
                mapElement: document.querySelector('.js-locator-map'),
                locatorList: document.querySelector('.js-locator-list'),
                searchInput: document.querySelector('.centers-locator__input'),
                searchBtn: document.querySelector('.js-locator-search-btn'),
                amenityCheckboxes: document.querySelectorAll('.centers-locator__filters .multiselect:not(.multiselect--insurers) input[type="checkbox"]'),
                insurerCheckboxes: document.querySelectorAll('.multiselect--insurers input[type="checkbox"]'),
                geoBlock: document.querySelector('.js-geolocate'),
                geoButton: document.querySelector('.js-geolocate-btn'),
                closeButton: document.querySelector('.js-geolocate-close'),
            };

            if (!this.elements.locatorWrapper) return;

            this.bindMethods();
            this.ui_initEventListeners();
            this.map_loadScript();
            this.api_fetchAllLocations(); // Fetch all locations on init
        },

        bindMethods() {
            window.initMap = this.map_initMap.bind(this);
            this.handlers_applyFiltersAndRender = this.handlers_applyFiltersAndRender.bind(this);
            this.ui_updateListVisibility = this.ui_updateListVisibility.bind(this);
        },

        // --- TIME CONVERSION (no changes needed here) ---
        time_parseDayIntervals(content) {
            if (!content || content.trim() === '' || content.trim().toLowerCase().endsWith("closed")) { return { closed: true, intervals: [] }; }
            const parts = content.trim().match(/^([A-Za-z]{2})\s(.+)$/);
            if (!parts) { return null; }
            const intervals = parts[2].split(',').map(range => {
                const times = range.trim().split('-');
                if (times.length !== 2) { return null; }
                return { startTime: times[0].substring(0, 5), endTime: times[1].substring(0, 5) };
            }).filter(Boolean);
            if (intervals.length === 0) { return { closed: true, intervals: [] }; }
            return { dayCode: parts[1], intervals, closed: false };
        },

        time_initializeTimeConversion(container) {
            const openingHoursBlocks = container.querySelectorAll('.center-card__chip--hours');
            const now = new Date();
            const isoDate = now.toISOString().split('T')[0];
            openingHoursBlocks.forEach(block => {
                const offsetString = block.dataset.offset;
                const dayContent = block.getAttribute('content');
                if (!offsetString || !dayContent) return;
                const dayData = this.time_parseDayIntervals(dayContent);
                if (dayData) {
                    this.time_updateDisplayTime(block, dayData, isoDate, offsetString);
                }
            });
        },

        time_updateDisplayTime(dayElement, dayData, isoDate, offsetString) {
            // Display time is rendered server-side (PHP). JS only applies open/closed badge.
        },


        // --- UI ---
        ui_initEventListeners() {
            if (this.elements.geoButton) {
                this.elements.geoButton.addEventListener('click', this.geo_requestGeoLocation.bind(this));
            }
            if (this.elements.closeButton) {
                this.elements.closeButton.addEventListener('click', () => {
                    if (this.elements.geoBlock) this.elements.geoBlock.hidden = true;
                });
            }

            if (this.elements.amenityCheckboxes) {
                this.elements.amenityCheckboxes.forEach(checkbox => {
                    checkbox.addEventListener('change', this.handlers_applyFiltersAndRender);
                });
            }
            if (this.elements.insurerCheckboxes) {
                this.elements.insurerCheckboxes.forEach(checkbox => {
                    checkbox.addEventListener('change', this.handlers_applyFiltersAndRender);
                });
            }

            if (this.elements.searchBtn) {
                this.elements.searchBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.map_searchFirstPrediction();
                });
            }
            if (this.elements.searchInput) {
                this.elements.searchInput.addEventListener('keydown', (e) => {
                    if (e.key !== 'Enter') return;
                    if (document.querySelector('.pac-item-selected')) return; // let Google's own selection fire place_changed
                    e.preventDefault();
                    this.map_searchFirstPrediction();
                });
            }
        },

        // --- MAP ---
        map_loadScript() {
            if (!this.config.apiKey) {
                console.error('Google Maps API Key is missing.');
                if (this.elements.mapElement) this.elements.mapElement.innerHTML = '<p style="text-align:center; padding: 20px;">' + this.config.strings.mapConfigError + '</p>';
                return;
            }
            if (typeof google !== 'undefined' && typeof google.maps !== 'undefined') {
                window.initMap();
                return;
            }
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${this.config.apiKey}&callback=initMap&libraries=places`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        },

        map_initMap() {
            if (!this.elements.mapElement) return;
            this.state.map = new google.maps.Map(this.elements.mapElement, {
                zoom: this.config.defaultZoom,
                center: this.config.defaultCenter,
                disableDefaultUI: true,
                zoomControl: true,
            });

            this.state.map.addListener('idle', this.ui_updateListVisibility);
            this.map_initAutocomplete();

            const clustererScript = document.createElement('script');
            clustererScript.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
            clustererScript.async = true;
            clustererScript.onload = () => {
                this.state.markerClusterer = new markerClusterer.MarkerClusterer({ map: this.state.map });
                if (this.state.pendingMapUpdate) {
                    this.state.pendingMapUpdate();
                }
            };
            document.head.appendChild(clustererScript);
        },

        autocompleteCountries: ['fr', 'gp', 'mq', 'gf', 're', 'yt', 'pm', 'bl', 'mf'],

        map_initAutocomplete() {
            if (!this.elements.searchInput) return;

            const autocomplete = new google.maps.places.Autocomplete(this.elements.searchInput, {
                types: ['geocode'],
                componentRestrictions: { country: this.autocompleteCountries },
            });

            this.services = {
                autocompleteService: new google.maps.places.AutocompleteService(),
                placesService: new google.maps.places.PlacesService(this.state.map),
            };

            autocomplete.addListener('place_changed', () => {
                this.map_handlePlaceResult(autocomplete.getPlace());
            });
        },

        map_handlePlaceResult(place) {
            this.state.userCoordinates = null;

            if (!place.geometry) {
                console.warn("Autocomplete: No details available for input: '" + place.name + "', attempting to geocode.");
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode(
                    {
                        'address': place.name,
                        // Geocoder.componentRestrictions.country only accepts a single code, unlike Autocomplete/AutocompleteService.
                        componentRestrictions: { country: this.autocompleteCountries[0] },
                    },
                    (results, status) => {
                        if (status === 'OK' && results[0]) {
                            if (results[0].geometry.viewport) {
                                this.state.map.fitBounds(results[0].geometry.viewport);
                            } else {
                                this.state.map.setCenter(results[0].geometry.location);
                                this.state.map.setZoom(17);
                            }
                            // The map 'idle' event will trigger the list update, so no need to call handlers.
                        } else {
                            console.warn('Geocode was not successful for the following reason: ' + status);
                        }
                    });
                return;
            }

            if (place.geometry.viewport) {
                this.state.map.fitBounds(place.geometry.viewport);
            } else {
                this.state.map.setCenter(place.geometry.location);
                this.state.map.setZoom(17);
            }
            this.handlers_applyFiltersAndRender(true);
        },

        // Used when the user validates via the search button or Enter without picking a
        // suggestion from the dropdown.
        //
        // A French commune name is resolved against the site's own commune table before
        // Google is asked anything. Google ranks predictions by prominence, not by exact
        // match: typing "Lux" returns Luxey, Luxeuil-les-Bains, Luxembourg and Luxe, but
        // never Lux (71100), and this used to keep predictions[0] blindly, recentring the
        // map 500 km away in the Landes and offering Mont-de-Marsan. Reported 31/08/2026.
        //
        // At least ten A+Glass towns are prefixes of better known communes (Lezignan,
        // Castanet, Bourgneuf, Rosieres, Bruyeres, Marly and others), so this was never
        // about Lux alone: each of those searches was quietly sent somewhere else.
        map_searchFirstPrediction() {
            const value = this.elements.searchInput?.value.trim();
            if (!value) return;

            this.map_lookupCommune(value)
                .then((commune) => {
                    if (commune) {
                        this.map_centerOnCommune(commune);
                        return;
                    }
                    this.map_searchViaGoogle(value);
                })
                .catch(() => this.map_searchViaGoogle(value));
        },

        /** Asks the site for a commune whose name matches the query exactly. */
        async map_lookupCommune(value) {
            if (!this.config.ajaxUrl) return null;

            const body = new FormData();
            body.append('action', 'ags_locate_commune');
            body.append('q', value);

            const response = await fetch(this.config.ajaxUrl, { method: 'POST', body });
            if (!response.ok) return null;

            const result = await response.json();
            return result?.success ? (result.data || null) : null;
        },

        /** Centres the map on a commune resolved from our own data. */
        map_centerOnCommune(commune) {
            this.state.userCoordinates = null;
            this.state.map.setCenter({ lat: commune.lat, lng: commune.lng });
            this.state.map.setZoom(11);
            this.handlers_applyFiltersAndRender(true);
        },

        // Fallback for everything the commune table does not answer: addresses,
        // postcodes, business names, anything outside mainland commune names.
        map_searchViaGoogle(value) {
            if (!this.services?.autocompleteService || !this.services?.placesService) {
                this.map_handlePlaceResult({ name: value });
                return;
            }

            this.services.autocompleteService.getPlacePredictions({
                input: value,
                types: ['geocode'],
                componentRestrictions: { country: this.autocompleteCountries },
            }, (predictions, status) => {
                if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions?.length) {
                    this.map_handlePlaceResult({ name: value });
                    return;
                }

                // Prefer a prediction that matches what was actually typed. Google ranks
                // by prominence, so an exact match sitting second used to be discarded in
                // favour of a bigger town.
                const normalise = (str) => (str || '')
                    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase().replace(/[^a-z0-9]/g, '');
                const typed = normalise(value);
                const exact = predictions.find(
                    (p) => normalise(p.structured_formatting?.main_text) === typed
                );
                const chosen = exact || predictions[0];

                this.services.placesService.getDetails(
                    { placeId: chosen.place_id, fields: ['geometry', 'name'] },
                    (place, detailsStatus) => {
                        if (detailsStatus !== google.maps.places.PlacesServiceStatus.OK || !place) {
                            this.map_handlePlaceResult({ name: value });
                            return;
                        }
                        this.map_handlePlaceResult(place);
                    }
                );
            });
        },


        // --- API & DATA ---
        async api_fetchAllLocations() {
            this.elements.locatorList.innerHTML = `<div class="centers-locator__loading"><p>${this.config.strings.loading}</p></div>`;
            this.elements.locatorList.classList.add('is-loading');

            const formData = new FormData();
            formData.append('action', 'get_all_locations');
            formData.append('nonce', this.config.nonce);
            formData.append('extendedVersion', this.config.extendedVersion);

            // Add taxonomy filters if they exist
            if (window.centerLocator?.taxonomyFilters && Object.keys(window.centerLocator.taxonomyFilters).length > 0) {
                formData.append('taxonomyFilters', JSON.stringify(window.centerLocator.taxonomyFilters));
            }

            try {
                const response = await fetch(this.config.ajaxUrl, { method: 'POST', body: formData });
                if (!response.ok) throw new Error('Network response was not ok.');
                const result = await response.json();

                if (result.success) {
                    this.elements.locatorList.innerHTML = result.data.join('');
                    this.state.allLocations = Array.from(this.elements.locatorList.querySelectorAll('.center-card')).map(cardElement => ({
                        id: cardElement.id.replace('center-', ''),
                        title: cardElement.dataset.title || '',
                        addressLines: cardElement.dataset.addressLines || '',
                        postalCode: cardElement.dataset.postalCode || '',
                        locality: cardElement.dataset.locality || '',
                        amenities: cardElement.dataset.amenities ? cardElement.dataset.amenities.split(',') : [],
                        insurers: cardElement.dataset.insurers ? cardElement.dataset.insurers.split(',') : [],
                        latitude: parseFloat(cardElement.dataset.lat) || null,
                        longitude: parseFloat(cardElement.dataset.lng) || null,
                        element: cardElement,
                        matchesPrimaryFilters: false, // Initialize filter match state
                    }));

                    // Add card click listeners once
                    this.state.allLocations.forEach(location => {
                        location.element.addEventListener('click', () => {
                            if (location.latitude && location.longitude) {
                                this.state.map.setCenter({ lat: location.latitude, lng: location.longitude });
                                this.state.map.setZoom(14);
                            }
                            this.elements.locatorList.querySelectorAll('.center-card').forEach(c => c.classList.remove('is-highlighted'));
                            location.element.classList.add('is-highlighted');
                        });
                    });

                    this.state.isDataLoaded = true;
                    this.time_initializeTimeConversion(this.elements.locatorList);
                    this.geo_initialize();

                } else {
                    throw new Error(result.data.message || this.config.strings.fetchError);
                }
            } catch (error) {
                console.error('Fetch error:', error);
                this.elements.locatorList.innerHTML = `<div class="centers-locator__error"><p>${this.config.strings.loadError}</p></div>`;
            } finally {
                this.elements.locatorList.classList.remove('is-loading');
            }
        },

        // --- GEOLOCATION ---
        geo_initialize() {
            if (!this.state.isDataLoaded) return;

            const hasRestoredState = this.state_load();
            if (hasRestoredState) {
                if (this.elements.geoBlock) this.elements.geoBlock.hidden = true;
                return;
            }

            if (this.config.customCenter) {
                this.handlers_applyFiltersAndRender();
                if (this.elements.geoBlock) this.elements.geoBlock.hidden = true;
                return;
            }

            if (navigator.permissions) {
                navigator.permissions.query({ name: 'geolocation' }).then(permissionStatus => {
                    if (permissionStatus.state === 'granted') {
                        this.geo_requestGeoLocation();
                    } else {
                        this.handlers_applyFiltersAndRender(); // Show all initially
                        if (permissionStatus.state === 'prompt') {
                            if (this.elements.geoBlock) this.elements.geoBlock.hidden = false;
                        }
                    }
                    permissionStatus.onchange = () => {
                        if (permissionStatus.state === 'denied' && this.elements.geoBlock) {
                            this.elements.geoBlock.hidden = true;
                        }
                    };
                });
            } else {
                this.handlers_applyFiltersAndRender();
                if (this.elements.geoBlock) this.elements.geoBlock.hidden = false;
            }
        },

        geo_requestGeoLocation() {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(this.geo_handleGeoSuccess.bind(this), this.geo_handleGeoError.bind(this));
            } else {
                alert(this.config.strings.geoNotSupported);
            }
        },

        geo_handleGeoSuccess(position) {
            const { latitude, longitude } = position.coords;
            this.state.userCoordinates = { latitude, longitude };
            if (this.elements.geoBlock) this.elements.geoBlock.hidden = true;
            this.handlers_applyFiltersAndRender();
        },

        geo_handleGeoError(error) {
            console.error('Geolocation error:', error);
            if (this.elements.geoBlock) this.elements.geoBlock.hidden = true;
            this.handlers_applyFiltersAndRender();
        },

        // --- HANDLERS & RENDERING ---

        // Called by map 'idle' event and after primary filters are applied
        ui_updateListVisibility() {
            if (!this.state.isDataLoaded || !this.state.map) return;

            const mapBounds = this.state.map.getBounds();
            let visibleLocations = 0;

            if (!mapBounds) {
                // Fallback for when map is not ready: just use primary filters.
                this.state.allLocations.forEach(location => {
                    if (location.matchesPrimaryFilters) {
                        location.element.style.display = '';
                        visibleLocations++;
                    } else {
                        location.element.style.display = 'none';
                    }
                });
            } else {
                this.state.allLocations.forEach(location => {
                    const inMapView = location.latitude && location.longitude ? mapBounds.contains({ lat: location.latitude, lng: location.longitude }) : false;
                    if (location.matchesPrimaryFilters && inMapView) {
                        location.element.style.display = '';
                        visibleLocations++;
                    } else {
                        location.element.style.display = 'none';
                    }
                });
            }

            // Handle sorting for visible items
            const mapCenter = this.state.map.getCenter();
            if (mapCenter) {
                this.state.allLocations.forEach(location => {
                    if (location.element.style.display !== 'none') {
                        location.distance = this.utils_calculateDistance(
                            mapCenter.lat(),
                            mapCenter.lng(),
                            location.latitude,
                            location.longitude
                        );
                    } else {
                        location.distance = Infinity;
                    }
                });

                const sortedVisibleElements = this.state.allLocations
                    .filter(loc => loc.element.style.display !== 'none')
                    .sort((a, b) => a.distance - b.distance);

                sortedVisibleElements.forEach(loc => {
                    this.elements.locatorList.appendChild(loc.element);
                });
            }

            // Handle "no results" message for the list
            const noResultsEl = this.elements.locatorList.querySelector('.centers-locator__no-results');
            // Remove any previously injected nearest-centers fallback
            const prevNearest = this.elements.locatorList.querySelector('.centers-locator__nearest-fallback');
            if (prevNearest) prevNearest.remove();

            if (visibleLocations === 0) {
                if (!noResultsEl) {
                    const newNoResultsEl = document.createElement('div');
                    newNoResultsEl.className = 'centers-locator__no-results';
                    newNoResultsEl.innerHTML = `<p>${this.config.strings.noResults}</p>`;
                    this.elements.locatorList.prepend(newNoResultsEl);
                }

                // Show the 2 nearest centers as fallback
                if (mapCenter) {
                    const nearest = this.state.allLocations
                        .filter(loc => loc.matchesPrimaryFilters && loc.latitude && loc.longitude)
                        .map(loc => ({
                            ...loc,
                            distance: this.utils_calculateDistance(
                                mapCenter.lat(), mapCenter.lng(),
                                loc.latitude, loc.longitude
                            )
                        }))
                        .sort((a, b) => a.distance - b.distance)
                        .slice(0, 2);

                    if (nearest.length > 0) {
                        const wrapper = document.createElement('div');
                        wrapper.className = 'centers-locator__nearest-fallback';
                        const title = document.createElement('p');
                        title.className = 'centers-locator__nearest-fallback-title';
                        title.textContent = 'Centres A+Glass les plus proches :';
                        wrapper.appendChild(title);

                        nearest.forEach(loc => {
                            const clone = loc.element.cloneNode(true);
                            clone.style.display = '';
                            // Show distance badge if not already present
                            const distKm = loc.distance < 10
                                ? loc.distance.toFixed(1)
                                : Math.round(loc.distance);
                            const badge = document.createElement('span');
                            badge.className = 'centers-locator__distance-badge';
                            badge.textContent = `${distKm} km`;
                            clone.prepend(badge);
                            wrapper.appendChild(clone);
                        });

                        this.elements.locatorList.appendChild(wrapper);
                    }
                }
            } else {
                if (noResultsEl) {
                    noResultsEl.remove();
                }
            }
            this.state_save();
        },

        // Applies search/amenity filters and triggers map/list updates
        handlers_applyFiltersAndRender(isAutocompleteSelection = false) {
            if (!this.state.isDataLoaded) return;

            const selectedAmenityIds = this.elements.amenityCheckboxes
                ? Array.from(this.elements.amenityCheckboxes)
                    .filter(cb => cb.checked)
                    .map(cb => cb.value)
                : [];

            const selectedInsurerIds = this.elements.insurerCheckboxes
                ? Array.from(this.elements.insurerCheckboxes)
                    .filter(cb => cb.checked)
                    .map(cb => cb.value)
                : [];

            this.state.allLocations.forEach(location => {
                // AND logic for amenities: location must have ALL selected amenities
                const amenityMatch = selectedAmenityIds.length === 0
                    ? true
                    : selectedAmenityIds.every(selectedId => location.amenities.some(id => String(id).trim() == String(selectedId).trim()));

                // OR logic for insurers: location must work with AT LEAST ONE selected insurer
                const insurerMatch = selectedInsurerIds.length === 0
                    ? true
                    : selectedInsurerIds.some(selectedId => location.insurers.some(id => String(id).trim() == String(selectedId).trim()));

                location.matchesPrimaryFilters = amenityMatch && insurerMatch;
            });

            this.map_updateMarkers(isAutocompleteSelection);
            this.ui_updateListVisibility();
            this.state_save();
        },

        map_updateMarkers(isAutocompleteSelection = false) {
            if (!this.state.markerClusterer) {
                this.state.pendingMapUpdate = () => this.map_updateMarkers(isAutocompleteSelection);
                return;
            }
            this.state.pendingMapUpdate = null;
            this.state.markerClusterer.clearMarkers();

            const geocoder = new google.maps.Geocoder();
            const bounds = new google.maps.LatLngBounds();
            const locationsToMark = this.state.allLocations.filter(loc => loc.matchesPrimaryFilters);

            if (locationsToMark.length === 0) {
                if (this.state.userCoordinates) {
                    this.state.map.setCenter({ lat: this.state.userCoordinates.latitude, lng: this.state.userCoordinates.longitude });
                    this.state.map.setZoom(this.config.userZoom);
                } else {
                    this.state.map.setCenter(this.config.defaultCenter);
                    this.state.map.setZoom(this.config.defaultZoom);
                }
                return;
            }

            const markerPromises = locationsToMark.map(location => {
                return new Promise(resolve => {
                    const card = location.element;
                    const createAndResolveMarker = (position) => {
                        if (!position) return resolve(null);
                        bounds.extend(position);
                        const marker = new google.maps.Marker({
                            position: position,
                            title: location.title,
                            icon: {
                                url: this.config.markerIcon.url,
                                scaledSize: new google.maps.Size(this.config.markerIcon.size.width, this.config.markerIcon.size.height)
                            }
                        });

                        marker.addListener('click', () => {
                            this.elements.locatorList.querySelectorAll('.center-card').forEach(c => c.classList.remove('is-highlighted'));
                            card.classList.add('is-highlighted');
                            card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        });
                        resolve(marker);
                    };

                    if (location.latitude && location.longitude) {
                        createAndResolveMarker({ lat: location.latitude, lng: location.longitude });
                    } else {
                        const address = [location.addressLines, location.locality, location.postalCode].filter(Boolean).join(', ');
                        if (address) {
                            geocoder.geocode({ 'address': address }, (results, status) => {
                                if (status === 'OK') {
                                    location.latitude = results[0].geometry.location.lat();
                                    location.longitude = results[0].geometry.location.lng();
                                    createAndResolveMarker(results[0].geometry.location);
                                } else {
                                    console.error(`Geocode failed for address "${address}": ${status}`);
                                    resolve(null);
                                }
                            });
                        } else {
                            resolve(null);
                        }
                    }
                });
            });

            Promise.all(markerPromises).then(resolvedMarkers => {
                const newMarkers = resolvedMarkers.filter(Boolean);
                this.state.markerClusterer.addMarkers(newMarkers);

                if (isAutocompleteSelection) {
                    return;
                }

                const isGeoSearch = this.state.userCoordinates !== null;
                const isTextSearch = this.elements.searchInput && this.elements.searchInput.value.trim().length > 2;

                if (this.state.savedMapPosition) {
                    const { lat, lng, zoom } = this.state.savedMapPosition;
                    this.state.map.setCenter({ lat, lng });
                    this.state.map.setZoom(zoom);
                    this.state.savedMapPosition = null;
                } else if (isGeoSearch) {
                    this.state.map.setCenter({ lat: this.state.userCoordinates.latitude, lng: this.state.userCoordinates.longitude });
                    this.state.map.setZoom(this.config.userZoom);
                } else if (isTextSearch && newMarkers.length > 0) {
                    this.state.map.fitBounds(bounds);
                    if (newMarkers.length === 1) {
                        this.state.map.setZoom(14);
                    }
                } else {
                    this.state.map.setCenter(this.config.defaultCenter);
                    this.state.map.setZoom(this.config.defaultZoom);
                }
            });
        },

        // --- UTILS ---
        utils_debounce(func, delay) {
            let timeout;
            return (...args) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), delay);
            };
        },

        utils_calculateDistance(lat1, lon1, lat2, lon2) {
            if (lat1 === null || lon1 === null || lat2 === null || lon2 === null) {
                return Infinity;
            }
            const R = 6371; // Radius of the earth in km
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;
            const a =
                0.5 - Math.cos(dLat) / 2 +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                (1 - Math.cos(dLon)) / 2;
            return R * 2 * Math.asin(Math.sqrt(a)); // Distance in km
        },

        // --- SESSION STATE PERSISTENCE ---
        state_getKey() {
            return `ags_locator_${this.config.extendedVersion ? 'internal' : 'public'}`;
        },

        state_save() {
            if (!this.state.isDataLoaded) return;
            const amenities = this.elements.amenityCheckboxes
                ? [...this.elements.amenityCheckboxes].filter(cb => cb.checked).map(cb => cb.value)
                : [];
            const insurers = this.elements.insurerCheckboxes
                ? [...this.elements.insurerCheckboxes].filter(cb => cb.checked).map(cb => cb.value)
                : [];
            const searchText = this.elements.searchInput?.value || '';
            const center = this.state.map?.getCenter();
            const zoom = this.state.map?.getZoom();
            sessionStorage.setItem(this.state_getKey(), JSON.stringify({
                amenities,
                insurers,
                searchText,
                mapLat: center ? center.lat() : null,
                mapLng: center ? center.lng() : null,
                mapZoom: zoom ?? null,
            }));
        },

        state_load() {
            const raw = sessionStorage.getItem(this.state_getKey());
            if (!raw) return false;
            let saved;
            try { saved = JSON.parse(raw); } catch { return false; }

            if (this.elements.amenityCheckboxes) {
                this.elements.amenityCheckboxes.forEach(cb => {
                    cb.checked = saved.amenities?.includes(cb.value) ?? false;
                });
            }
            if (this.elements.insurerCheckboxes) {
                this.elements.insurerCheckboxes.forEach(cb => {
                    cb.checked = saved.insurers?.includes(cb.value) ?? false;
                });
            }

            if (this.elements.searchInput && saved.searchText) {
                this.elements.searchInput.value = saved.searchText;
            }

            if (saved.mapLat && saved.mapLng && saved.mapZoom) {
                this.state.savedMapPosition = { lat: saved.mapLat, lng: saved.mapLng, zoom: saved.mapZoom };
            }

            // Fire one change event per multiselect group to update dropdown trigger texts
            // and trigger handlers_applyFiltersAndRender (updateTriggerText is a closure, not accessible directly)
            const firstAmenity = this.elements.amenityCheckboxes?.[0];
            const firstInsurer = this.elements.insurerCheckboxes?.[0];
            if (firstAmenity) firstAmenity.dispatchEvent(new Event('change', { bubbles: true }));
            if (firstInsurer) firstInsurer.dispatchEvent(new Event('change', { bubbles: true }));

            return true;
        }
    };

    CenterLocatorApp.init();
});


document.addEventListener('DOMContentLoaded', (e) => {
    const multiselect = document.querySelectorAll('.multiselect');
    if (multiselect) {
        multiselect.forEach(container => {
            const triggerText = container.querySelector(".trigger-text");
            const selectTrigger = container.querySelector(".select-trigger");
            const checkboxes = container.querySelectorAll('input[type="checkbox"]');
            const clearBtn = container.querySelector('.clear-btn');

            selectTrigger.addEventListener('click', () => {
                console.info('click');
                container.classList.toggle("active");
            });

            clearBtn.addEventListener('click', clearAll);

            function updateTriggerText() {
                const selectedCount = container.querySelectorAll(
                    'input[type="checkbox"]:checked'
                ).length;

                if (selectedCount > 0) {
                    const filterWord = selectedCount > 1 ? triggerText.dataset.many : triggerText.dataset.one;
                    triggerText.textContent = `${selectedCount} ${filterWord} ${triggerText.dataset.selected}`;
                    triggerText.classList.add("has-selection");
                } else {
                    triggerText.textContent = triggerText.dataset.default;
                    triggerText.classList.remove("has-selection");
                }
            }

            function clearAll() {
                checkboxes.forEach((cb) => {
                    cb.checked = false;
                });
                updateTriggerText();

                if (checkboxes.length > 0) {
                    checkboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
                }
            }

            document.addEventListener("click", function (e) {
                if (!container.contains(e.target)) {
                    container.classList.remove("active");
                }
            });

            checkboxes.forEach((cb) => {
                cb.addEventListener("change", updateTriggerText);
            });

            updateTriggerText();
        });
    }
});
